<template>
  <aside v-if="config" ref="card" class="interactive-slot" :class="{'interactive-slot--visible': imageLoaded}"
    :style="positionStyle" aria-label="互動圖卡">
    <section v-if="expanded" class="interactive-slot__panel" :style="frameStyle" aria-label="互動內容">
      <header v-if="!frameReady || frameError"><span>互動內容</span><button type="button" aria-label="縮合互動內容" @click="collapse">×</button></header>
      <p v-if="frameError" role="alert">暫時無法開啟。<button v-if="!frameVisible" type="button" @click="startFrame">重試</button><span v-else>請縮合後重試。</span></p>
      <p v-else-if="!frameReady && !authExpired" role="status">{{ frameSlow ? '連線較慢，仍在等待問卷載入。可縮合後重試。' : '載入中…' }}</p>
      <p v-if="authExpired" role="alert">登入授權已失效，請重新登入 CRM 後再開啟問卷。</p>
      <p v-else-if="tokenRefreshError" role="alert">授權更新失敗，答案畫面已保留。<button type="button" @click="sendFrameContext">重試授權</button></p>
      <iframe v-if="frameVisible && !frameError" :key="responseId" ref="frame" :src="iframeUrl"
        :srcdoc="mockDocument" title="互動問卷" sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerpolicy="no-referrer" :style="{visibility: frameReady ? 'visible' : 'hidden'}" @load="diagnoseFrame('document-loaded')" />
    </section>
    <template v-else>
      <a v-if="config.action.type === 'link'" ref="trigger" :href="config.action.url" target="_blank" rel="noopener noreferrer"
        aria-label="開啟圖卡連結" @click="clickLink">
        <img :src="config.preview.url" alt="功能推播圖卡" width="208" height="208" @load="imageReady" @error="imageFailed">
      </a>
      <button v-else ref="trigger" type="button" class="interactive-slot__cover" aria-label="開啟互動內容" @click="openFrame">
        <img :src="config.preview.url" alt="功能推播圖卡" width="208" height="208" @load="imageReady" @error="imageFailed">
      </button>
      <button v-if="imageLoaded" type="button" class="interactive-slot__close" aria-label="關閉圖卡" @click="closeCard">×</button>
    </template>
  </aside>
</template>

<script>
import { mapGetters } from 'vuex'
import { createInteractiveSlotClient } from '@/api/slot-interaction'
import { SLOT_DEFINITIONS, DEFAULT_FRAME_SIZE, FRAME_READY_TIMEOUT, newSlotId, createSlotEvent,
  frameMessage, frameInit, frameUrl, tokenRefreshDelay, clampFrameSize } from '@/utils/slotInteraction'

export default {
  name: 'InteractiveSlotCard',
  data() {
    return {
      client: createInteractiveSlotClient(), config: null, imageLoaded: false, viewTracked: false,
      sequence: 0, expanded: false, responseId: null, frameVisible: false, frameReady: false,
      frameError: false, frameSlow: false, authExpired: false, tokenRefreshError: false, initializing: false, clickEvent: null, frameSize: { ...DEFAULT_FRAME_SIZE },
      viewport: { width: window.innerWidth, height: window.innerHeight },
    }
  },
  computed: {
    ...mapGetters(['axiosParamData', 'liffUser']),
    definition() {
      return SLOT_DEFINITIONS.find(d => this.$route.matched.some(r => r.name === d.routeName)) || null
    },
    identity() {
      const a = this.axiosParamData || {}
      if (!['1', '2'].includes(String(a.type))) return null
      const code = String(a.type) === '1' ? a.clinic_code : a.doctor_code
      if (!code) return null
      return { viewer: a.crm_user_id == null ? null : String(a.crm_user_id), clinic_id: code ? String(code) : null,
        id_type: code ? (String(a.type) === '1' ? 'clinic' : 'doctor') : null }
    },
    eventIdentity() { return (this.config && this.config.context) || this.client.testIdentity || this.identity },
    iframeUrl() { return this.config && this.responseId ? frameUrl(this.config.action.url, window.location.origin, this.responseId) : undefined },
    contextKey() {
      if (!this.definition || !this.identity || this.liffUser || !this.$vuetify.breakpoint.mdAndUp) return ''
      return JSON.stringify([this.definition.slot, this.identity, this.$store.state && this.$store.state.user && this.$store.state.user.token])
    },
    offset() {
      const positions = { home: { left: 0, bottom: 76 }, 'online-reserve': { left: 20, bottom: 52 },
        reschedule: { left: 20, bottom: 104 }, review: { left: 0, bottom: 0 } }
      return positions[this.definition && this.definition.position] || { left: 20, bottom: 20 }
    },
    positionStyle() { return this.expanded ? { left: '16px', bottom: '16px' } : { left: `${this.offset.left}px`, bottom: `${this.offset.bottom}px` } },
    frameStyle() {
      const size = clampFrameSize(this.frameSize, this.viewport, this.offset)
      return { width: `${size.width}px`, height: `${size.height}px` }
    },
    frameOrigin() { return this.config ? new URL(this.config.action.url).origin : '' },
    mockDocument() { return this.client.frameDocument ? this.client.frameDocument(this.responseId) : undefined },
  },
  watch: { contextKey: { immediate: true, handler() { this.loadConfig() } } },
  mounted() {
    window.addEventListener('message', this.onMessage)
    window.addEventListener('resize', this.onResize)
    window.addEventListener('focus', this.refreshOnFocus)
  },
  beforeDestroy() {
    this.sequence++
    this.reset()
    window.removeEventListener('message', this.onMessage)
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('focus', this.refreshOnFocus)
  },
  methods: {
    diagnoseFrame(stage, status) {
      // 本機追查偶發載入延遲，僅記階段／時間／狀態碼，不輸出身分或 token。
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.info('[Slot timing]', stage, Date.now(), status || '')
    },
    current(sequence, key) { return this.sequence === sequence && this.contextKey === key },
    async loadConfig() {
      const sequence = ++this.sequence, key = this.contextKey
      this.reset()
      if (!key) return
      try {
        const config = await this.client.getConfig(this.definition.slot, () => this.current(sequence, key))
        if (!this.current(sequence, key) || !config || this.isClosed(config)) return
        this.config = config
      } catch {
        // 入口失敗不阻擋 CRM；也不回退舊廣告或輸出含 token 的 Axios 錯誤。
      }
    },
    imageReady() {
      this.imageLoaded = true
      this.$nextTick(() => {
        if (!this.$refs.card || this.viewTracked) return
        if (typeof IntersectionObserver === 'undefined') return this.trackView()
        this.disconnectObserver()
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(e => e.isIntersecting && e.intersectionRatio >= 0.5)) this.trackView()
        }, { threshold: [0.5] })
        this.observer.observe(this.$refs.card)
      })
    },
    imageFailed() { this.sequence++; this.reset() },
    trackView() {
      if (this.viewTracked || !this.config || !this.imageLoaded) return
      this.viewTracked = true
      this.disconnectObserver()
      this.track('view')
    },
    track(type, responseId = null) {
      if (!this.config || !this.identity || !this.definition) return
      const event = createSlotEvent(this.config, this.eventIdentity, this.definition.page, type, responseId)
      const sequence = this.sequence, key = this.contextKey
      this.client.recordEvent(event, () => this.current(sequence, key)).catch(() => {})
    },
    clickLink() { this.track('click') },
    async openFrame() {
      if (!this.imageLoaded || !this.config || this.expanded) return
      this.responseId = newSlotId()
      this.clickEvent = createSlotEvent(this.config, this.eventIdentity, this.definition.page, 'click', this.responseId)
      this.expanded = true
      this.frameSize = { ...DEFAULT_FRAME_SIZE }
      await this.startFrame()
    },
    async startFrame() {
      if (this.initializing || !this.expanded || this.frameVisible) return
      const sequence = this.sequence, key = this.contextKey, id = this.responseId
      const active = () => this.current(sequence, key) && this.responseId === id && this.expanded
      this.initializing = true
      this.frameError = false
      this.frameSlow = false
      try {
        await this.client.recordEvent(this.clickEvent, active)
        if (!active()) return
        this.frameVisible = true
        this.diagnoseFrame('frame-created')
        this.readyTimer = setTimeout(() => {
          // 載入較慢時保留 iframe，避免中止連線後永遠收不到 ready。
          if (active() && !this.frameReady) { this.diagnoseFrame('ready-timeout'); this.frameSlow = true }
        }, FRAME_READY_TIMEOUT)
      } catch { if (active()) this.frameError = true }
      finally { if (active()) this.initializing = false }
    },
    async onMessage(event) {
      if (!this.expanded || this.frameError) return
      const frame = this.$refs.frame
      const origin = this.mockDocument ? window.location.origin : this.frameOrigin
      const message = frameMessage(event, frame && frame.contentWindow, origin, this.responseId)
      if (!message) return
      if (message.type === 'request_collapse') return this.collapse()
      if (message.type === 'request_resize' && this.frameReady) {
        this.frameSize = { width: 420, height: message.height }
        return
      }
      if (message.type !== 'ready' || this.frameReady || this.initializing) return
      this.diagnoseFrame('ready-received')
      clearTimeout(this.readyTimer)
      this.frameSlow = false
      await this.sendFrameContext()
    },
    async sendFrameContext() {
      if (!this.expanded || !this.frameVisible || this.initializing || this.frameError || this.authExpired) return
      const frame = this.$refs.frame
      if (!frame) return
      const origin = this.mockDocument ? window.location.origin : this.frameOrigin
      this.initializing = true
      clearTimeout(this.tokenTimer)
      const wasReady = this.frameReady
      const sequence = this.sequence, key = this.contextKey, id = this.responseId
      const active = () => this.current(sequence, key) && this.responseId === id && this.expanded && !this.frameError
      try {
        const token = await this.client.getFrameToken({ channel: 'crm', slot: this.config.slot,
          creative_id: this.config.creative_id, response_id: id, identity: this.eventIdentity }, { refresh: wasReady })
        if (!active()) return
        if (typeof token !== 'string' || !token) throw new Error('缺少廣告授權')
        this.diagnoseFrame('token-received')
        frame.contentWindow.postMessage(frameInit(this.config, this.eventIdentity, this.definition.page, id, token), origin)
        this.frameReady = true
        this.tokenRefreshError = false
        const delay = tokenRefreshDelay(token)
        if (delay !== null) this.tokenTimer = setTimeout(this.sendFrameContext, delay)
        clearTimeout(this.readyTimer)
        if (!wasReady) frame.focus()
      } catch (error) {
        this.diagnoseFrame('init-failed', error.response && error.response.status)
        if (active()) {
          if (error.response && error.response.status === 401) {
            this.authExpired = true
            clearTimeout(this.readyTimer)
            return
          }
          if (!wasReady) this.frameError = true
          else {
            this.tokenRefreshError = true
            this.tokenTimer = setTimeout(this.sendFrameContext, 30000)
          }
        }
      } finally {
        if (this.responseId === id) this.initializing = false
      }
    },
    refreshOnFocus() { if (this.frameReady) this.sendFrameContext() },
    collapse() {
      this.resetFrame()
      this.$nextTick(() => { if (this.$refs.trigger) this.$refs.trigger.focus() })
    },
    closeKey(config) { return `floating_slot_closed:v2:${config.slot}:${config.creative_id}` },
    isClosed(config) {
      try {
        const age = Date.now() - Number(localStorage.getItem(this.closeKey(config)))
        return age >= 0 && age < 72 * 60 * 60 * 1000
      } catch { return false }
    },
    closeCard() {
      this.track('close')
      try { localStorage.setItem(this.closeKey(this.config), String(Date.now())) } catch { /* 當次仍可關閉 */ }
      // 不增加 sequence，讓剛送出的 close 完成；切換帳號仍會使請求失效。
      this.reset()
    },
    onResize() { this.viewport = { width: window.innerWidth, height: window.innerHeight } },
    disconnectObserver() { if (this.observer) this.observer.disconnect(); this.observer = null },
    resetFrame() {
      clearTimeout(this.readyTimer)
      clearTimeout(this.tokenTimer)
      this.tokenRefreshError = false
      this.authExpired = false
      this.clickEvent = null
      this.expanded = false
      this.frameVisible = false
      this.frameReady = false
      this.frameError = false
      this.frameSlow = false
      this.initializing = false
      this.responseId = null
    },
    reset() {
      this.disconnectObserver()
      this.resetFrame()
      this.config = null
      this.imageLoaded = false
      this.viewTracked = false
    },
  },
}
</script>

<style scoped>
.interactive-slot { position: fixed; z-index: 90; opacity: 0; pointer-events: none; }
.interactive-slot--visible { opacity: 1; pointer-events: auto; }
.interactive-slot__cover { display: block; padding: 0; border: 0; background: transparent; cursor: pointer; }
.interactive-slot img { display: block; width: 208px; height: 208px; object-fit: cover; }
.interactive-slot button:focus-visible, .interactive-slot a:focus-visible { outline: 3px solid #00a199; outline-offset: 2px; }
.interactive-slot__close { position: absolute; right: 4px; top: 4px; width: 32px; height: 32px; border-radius: 50%; background: white; font-size: 24px; }
.interactive-slot__panel { display: flex; flex-direction: column; background: white; border: 1px solid #ddd; border-radius: 12px; box-shadow: 0 6px 24px #0003; overflow: hidden; }
.interactive-slot__panel header { flex: 0 0 40px; display: flex; align-items: center; justify-content: space-between; padding-left: 16px; border-bottom: 1px solid #eee; }
.interactive-slot__panel header button { width: 40px; height: 40px; font-size: 24px; }
.interactive-slot__panel p { padding: 16px; margin: 0; }
.interactive-slot__panel iframe { flex: 1; width: 100%; min-height: 0; border: 0; }
</style>
